import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

/**
 * Citation TipTap Extension
 *
 * Renders citation markers [N] as non-editable and non-removable inline nodes.
 * Citations appear as atomic units that cannot be edited or deleted.
 */
export const Citation = Node.create({
  name: "citation",

  group: "inline",

  inline: true,

  // Atomic means the node is treated as a single unit - cursor skips over it
  atom: true,

  // Prevent selection of citation nodes
  selectable: false,

  // Prevent dragging
  draggable: false,

  addAttributes() {
    return {
      number: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-citation-number"),
        renderHTML: (attributes) => {
          return {
            "data-citation-number": attributes.number,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "span[data-citation]",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-citation": "",
        class: "citation-marker",
        contenteditable: "false",
      }),
      `[${node.attrs.number}]`,
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("citationProtection"),
        filterTransaction: (transaction, state) => {
          // Allow non-document-changing transactions (selection, focus, etc.)
          if (!transaction.docChanged) {
            return true;
          }

          // Allow programmatic content updates (setContent, paste handling, etc.)
          // These typically have specific meta flags or replace the entire document
          const meta = transaction.getMeta("uiEvent");
          if (!meta) {
            // Check if this is a full document replacement (programmatic update)
            // by checking if the transaction replaces from position 0
            let isFullReplace = false;
            transaction.steps.forEach((step) => {
              const json = step.toJSON();
              if (json.stepType === "replace" && json.from === 0) {
                isFullReplace = true;
              }
            });
            if (isFullReplace) {
              return true;
            }
          }

          // Count citations before and after the transaction
          let beforeCount = 0;
          state.doc.descendants((node) => {
            if (node.type.name === "citation") {
              beforeCount++;
            }
          });

          let afterCount = 0;
          transaction.doc.descendants((node) => {
            if (node.type.name === "citation") {
              afterCount++;
            }
          });

          // Block if citations would be deleted
          if (afterCount < beforeCount) {
            return false;
          }

          return true;
        },
      }),
    ];
  },
});
